import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import logoDark from "@/assets/brand/logo-full-dark.png";
import logoLight from "@/assets/brand/logo-full-light.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/services", label: "Services" },
  { to: "/estimator", label: "Estimator" },
  { to: "/assistant", label: "AI Assistant" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const onHero = loc.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const tone = onHero ? "text-white" : "text-[#1C1A17]";
  const muted = onHero ? "text-white/75 hover:text-white" : "text-[#5C5752] hover:text-[#1C1A17]";

  return (
    <header className={`sticky top-0 z-40 transition-all duration-500 ${
      onHero ? "bg-transparent" : "backdrop-blur-xl bg-[#FBF8F2]/85 border-b border-[#E8E2D5]"
    }`}>
      <div className="px-6 md:px-12 lg:px-20 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" aria-label="Dreamy Interiors — home" className="flex items-center shrink-0">
          <img
            src={onHero ? logoLight : logoDark}
            alt="Dreamy Interiors"
            className="h-14 md:h-16 w-auto select-none"
            draggable="false"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.25em] transition-colors link-underline ${isActive ? tone : muted}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          {user && user.role === "admin" && (
            <>
              <button
                data-testid="nav-admin-btn"
                onClick={() => nav("/admin")}
                className={`text-[11px] uppercase tracking-[0.25em] ${muted}`}
              >Admin</button>
              <button data-testid="nav-logout-btn" onClick={() => { logout(); nav("/"); }} className={`text-[11px] uppercase tracking-[0.25em] ${muted}`}>Logout</button>
            </>
          )}
          <Link
            data-testid="nav-cta-book"
            to="/book"
            className={onHero
              ? "rounded-full bg-white text-[#1C1A17] hover:bg-[#E8D9B3] px-6 py-3 text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-2 transition-all"
              : "rounded-full bg-[#1C1A17] text-white hover:bg-[#2D2925] px-6 py-3 text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-2 transition-all"
            }
          >
            Book Consultation <ArrowRight size={12} />
          </Link>
        </div>

        <button data-testid="nav-mobile-toggle" className={`lg:hidden p-2 ${tone}`} onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E8E2D5] bg-[#FBF8F2]">
          <div className="px-6 py-8 space-y-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="block text-sm uppercase tracking-[0.25em] text-[#1C1A17]"
              >{l.label}</NavLink>
            ))}
            <Link data-testid="mobile-cta-book" to="/book" className="btn-primary w-full justify-center">
              Book Consultation
            </Link>
            {user && user.role === "admin" && (
              <button onClick={() => nav("/admin")} className="block text-sm uppercase tracking-[0.25em] text-[#1C1A17]">Admin</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
